import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Trophy, TrendingUp, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function DiagnosticResults() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Diagnostic Results | Practice4SAT';
  }, []);
  const [score, setScore] = useState(null);
  const [rwScore, setRwScore] = useState(null);
  const [mathScore, setMathScore] = useState(null);
  const [scoreRanges, setScoreRanges] = useState(null);
  const [email, setEmail] = useState('');
  const [showPackageModal, setShowPackageModal] = useState(false);

  useEffect(() => {
    const storedScore = localStorage.getItem('diagnostic_final_score');
    const storedRwScore = localStorage.getItem('diagnostic_rw_score');
    const storedMathScore = localStorage.getItem('diagnostic_math_score');
    const storedScoreRanges = localStorage.getItem('diagnostic_score_ranges');
    const storedEmail = localStorage.getItem('diagnostic_email');
    
    if (!storedScore || !storedEmail) {
      navigate('/');
      return;
    }
    
    setScore(parseInt(storedScore));
    if (storedRwScore) setRwScore(parseInt(storedRwScore));
    if (storedMathScore) setMathScore(parseInt(storedMathScore));
    if (storedScoreRanges) {
      try {
        setScoreRanges(JSON.parse(storedScoreRanges));
      } catch (e) {
        console.error('Error parsing score ranges:', e);
      }
    }
    setEmail(storedEmail);
  }, []);

  if (!score) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading results...</div>
      </div>
    );
  }

  const handleLogoClick = () => {
    // Clear only diagnostic-related data
    localStorage.removeItem('diagnostic_session_id');
    localStorage.removeItem('diagnostic_email');
    localStorage.removeItem('diagnostic_questions');
    localStorage.removeItem('diagnostic_final_score');
    localStorage.removeItem('diagnostic_rw_score');
    localStorage.removeItem('diagnostic_math_score');
    localStorage.removeItem('diagnostic_score_ranges');
    navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header variant="minimal" onLogoClick={handleLogoClick} />

      {/* Main Content */}
      <div style={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        padding: 'clamp(1.5rem, 5vw, 3rem)', 
        maxWidth: '600px', 
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
        margin: '0 auto',
        boxSizing: 'border-box'
      }}>
        {/* Trophy Icon */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '1.5rem' 
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '50%',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            display: 'inline-flex'
          }}>
            <Trophy size={window.innerWidth < 480 ? 32 : 48} color="white" />
          </div>
        </div>

        {/* Title */}
        <h1 style={{ 
          fontSize: 'clamp(1.5rem, 5vw, 2rem)', 
          fontWeight: '700', 
          marginBottom: '0.5rem',
          color: '#1a202c'
        }}>
          Diagnostic Test Complete!
        </h1>

        <p style={{ 
          fontSize: '1rem', 
          color: '#718096',
          marginBottom: '2rem'
        }}>
          {email}
        </p>

        {/* Score Display */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: 'clamp(1rem, 4vw, 2rem)',
          marginBottom: '1rem'
        }}>
          <p style={{ 
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '0.5rem'
          }}>
            Your Total SAT Score
          </p>
          <div style={{ 
            fontSize: 'clamp(2.5rem, 10vw, 4rem)', 
            fontWeight: '700',
            color: 'white',
            lineHeight: 1
          }}>
            {score}
          </div>
          {scoreRanges?.total && (
            <p style={{ 
              fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
              color: 'rgba(255,255,255,0.9)',
              marginTop: '0.5rem',
              fontWeight: '500'
            }}>
              Range: {scoreRanges.total.min} - {scoreRanges.total.max}
            </p>
          )}
          <p style={{ 
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.7)',
            marginTop: '0.25rem'
          }}>
            out of 1600
          </p>
        </div>

        {/* Section Scores */}
        {(rwScore || mathScore) && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: '0.75rem', 
            marginBottom: '1.5rem' 
          }}>
            {/* R&W Score */}
            <div style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '12px',
              padding: 'clamp(1rem, 3vw, 1.5rem)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 'clamp(0.7rem, 2vw, 0.85rem)', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem' }}>
                Reading & Writing
              </div>
              <div style={{ 
                fontSize: 'clamp(1.75rem, 6vw, 2.5rem)', 
                fontWeight: '700', 
                color: 'white',
                lineHeight: 1
              }}>
                {rwScore || 'N/A'}
              </div>
              {scoreRanges?.rw && (
                <div style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.8rem)', color: 'rgba(255,255,255,0.9)', marginTop: '0.25rem', fontWeight: '500' }}>
                  Range: {scoreRanges.rw.min} - {scoreRanges.rw.max}
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.15rem' }}>
                out of 800
              </div>
            </div>

            {/* Math Score */}
            <div style={{ 
              background: 'linear-gradient(135deg, #0030b3 0%, #3b82f6 100%)',
              borderRadius: '12px',
              padding: 'clamp(1rem, 3vw, 1.5rem)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 'clamp(0.7rem, 2vw, 0.85rem)', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem' }}>
                Math
              </div>
              <div style={{ 
                fontSize: 'clamp(1.75rem, 6vw, 2.5rem)', 
                fontWeight: '700', 
                color: 'white',
                lineHeight: 1
              }}>
                {mathScore || 'N/A'}
              </div>
              {scoreRanges?.math && (
                <div style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.8rem)', color: 'rgba(255,255,255,0.9)', marginTop: '0.25rem', fontWeight: '500' }}>
                  Range: {scoreRanges.math.min} - {scoreRanges.math.max}
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.15rem' }}>
                out of 800
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div style={{ 
          background: '#f7fafc',
          borderRadius: '12px',
          padding: 'clamp(1rem, 4vw, 2rem)',
          marginBottom: '1.5rem',
          textAlign: 'left'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <TrendingUp size={20} color="#667eea" />
            <h2 style={{ fontSize: 'clamp(1.1rem, 4vw, 1.5rem)', fontWeight: '600', color: '#1a202c', margin: 0 }}>
              Want to improve your score?
            </h2>
          </div>
          
          <p style={{ 
            fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
            color: '#4a5568',
            marginBottom: '1rem',
            lineHeight: '1.6'
          }}>
            Sign up now to access <strong>20 full-length adaptive tests</strong>, detailed analytics, 
            question-by-question review, and personalized practice recommendations to boost your SAT score.
          </p>

          <ul style={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: '0 0 1rem 0',
            color: '#4a5568',
            fontSize: 'clamp(0.8rem, 2.5vw, 0.95rem)'
          }}>
            <li style={{ marginBottom: '0.4rem' }}>✓ 20 adaptive practice tests</li>
            <li style={{ marginBottom: '0.4rem' }}>✓ Detailed performance analytics</li>
            <li style={{ marginBottom: '0.4rem' }}>✓ Review all questions with explanations</li>
            <li style={{ marginBottom: '0.4rem' }}>✓ Track your progress over time</li>
          </ul>

          <Button 
            onClick={() => setShowPackageModal(true)}
            style={{ 
              width: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: 'clamp(0.75rem, 2vw, 1rem)',
              fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Sign Up to Access 20 Tests
          </Button>
        </div>

        {/* Secondary Action */}
        <Button 
          variant="outline"
          onClick={() => {
            // Clear only diagnostic-related data
            localStorage.removeItem('diagnostic_session_id');
            localStorage.removeItem('diagnostic_email');
            localStorage.removeItem('diagnostic_questions');
            localStorage.removeItem('diagnostic_final_score');
            navigate('/');
          }}
          style={{ width: '100%' }}
        >
          Return to Home
        </Button>
      </div>
      </div>

      <Footer />

      {/* Package Selection Modal */}
      <Dialog open={showPackageModal} onOpenChange={setShowPackageModal}>
        <DialogContent 
          style={{ 
            maxWidth: 'min(95vw, 750px)', 
            width: '95vw',
            maxHeight: '90vh', 
            overflow: 'auto',
            padding: '1.5rem',
            zIndex: 9999 
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.5rem', textAlign: 'center', marginBottom: '0.5rem', paddingRight: '2rem' }}>
              Get Full Access
            </DialogTitle>
            <p style={{ textAlign: 'center', color: '#666', fontSize: '0.95rem' }}>
              Free • No credit card needed
            </p>
          </DialogHeader>

          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            marginTop: '1rem' 
          }}>
            {/* Full Access Package */}
            <div 
              onClick={() => {
                localStorage.removeItem('diagnostic_questions');
                sessionStorage.setItem('openRegisterModal', 'true');
                sessionStorage.setItem('selectedPackage', 'full');
                navigate('/');
              }}
              style={{
                border: '2px solid #6366f1',
                borderRadius: '12px',
                padding: '1.5rem',
                cursor: 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '400px',
                width: '100%',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a202c', marginBottom: '0.25rem' }}>
                  Full Access
                </h3>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: '700', color: '#d1d5db', textDecoration: 'line-through' }}>$49</span>
                  <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#059669' }}>Free</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#666' }}>No credit card needed</p>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.9rem', flex: 1 }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Diagnostic baseline test</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>8 full-length adaptive SAT tests</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Personalized performance analytics</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Calibrated score prediction with confidence band</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Evolving performance-based study plan</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Plan-aligned personalized practice</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Full question bank access</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>Step-by-step expert solutions</span>
                </li>
              </ul>

              <Button 
                style={{ 
                  width: '100%',
                  marginTop: '1.5rem',
                  background: '#6366f1',
                  color: 'white',
                  fontWeight: '600',
                  padding: '0.85rem',
                  fontSize: '1rem'
                }}
              >
                Sign Up Free
              </Button>
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: '#666', marginTop: '1rem' }}>
            Free for a limited time
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
